import styled, { keyframes } from 'styled-components'

import { theme } from 'styles/theme'

const { colors, media, radii, space, zIndices } = theme

const flow = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`

const flowReverse = keyframes`
  from { transform: translateX(-50%); }
  to { transform: translateX(0); }
`

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space['spacing-md']};
  width: 100%;
`

export const Vessel = styled.div<{ $isDragging: boolean }>`
  position: relative;
  isolation: isolate;
  width: 100%;
  max-width: 26rem;
  height: 35rem;
  overflow: hidden;
  background-color: transparent;
  border: ${theme.borderWidths['border-stroke-micro']} solid #fff;
  border-radius: ${radii['corner-radius-lg']};
  touch-action: none;
  user-select: none;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};

  &:focus-visible {
    outline: ${theme.borderWidths['border-stroke-small']} solid ${colors['cashme-blue-neon']};
    outline-offset: ${space['spacing-xxxs']};
  }

  @media (${media.phone}) {
    height: 24rem;
  }
`

// Decorative graduation marks down the left edge (like a measuring beaker).
export const Ticks = styled.span`
  position: absolute;
  top: ${space['spacing-md']};
  bottom: ${space['spacing-md']};
  left: ${space['spacing-lg']};
  width: 1.75rem;
  pointer-events: none;
  background-image: repeating-linear-gradient(
      to bottom,
      ${colors['cashme-light-cinza']} 0,
      ${colors['cashme-light-cinza']} 0.125rem,
      transparent 0.125rem,
      transparent 5.25rem
    ),
    repeating-linear-gradient(
      to bottom,
      ${colors['cashme-light-cinza']} 0,
      ${colors['cashme-light-cinza']} 0.0625rem,
      transparent 0.0625rem,
      transparent 0.875rem
    );
  opacity: 0.5;
  z-index: ${zIndices[3]};
`

export const Water = styled.div<{ $isDragging: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  z-index: ${zIndices[1]};
  background-color: ${colors['blue-background-strong-default']};
  transition: ${({ $isDragging }) => ($isDragging ? 'none' : 'transform 120ms ease-out')};
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const WaveLayer = styled.span`
  position: absolute;

  /* Sits just above the water surface (slight overlap avoids a seam), so the
     undulating fill — not a flat edge — becomes the visible water top. */
  bottom: calc(100% - 0.125rem);
  left: 0;
  width: 200%;
  pointer-events: none;
  will-change: transform;
  backface-visibility: hidden;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`

export const WaveNeon = styled(WaveLayer)<{ $isDragging?: boolean }>`
  z-index: ${zIndices[1]};
  height: 1.67rem;
  animation: ${flowReverse} 3.5s linear infinite;
  animation-play-state: running;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const WaveBlue = styled(WaveLayer)<{ $isDragging?: boolean }>`
  z-index: ${zIndices[2]};
  height: 1.4rem;
  animation: ${flow} 5s linear infinite;
  animation-play-state: running;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const Value = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 22%;
  z-index: ${zIndices[2]};
  padding: 0 ${space['spacing-md']};
  text-align: center;
  pointer-events: none;
`

export const Grip = styled.span<{ $alignRight?: boolean; $interactive?: boolean }>`
  position: absolute;
  top: 0;
  left: ${({ $alignRight }) => ($alignRight ? '85%' : '50%')};
  z-index: ${zIndices[3]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  transform: translate(-50%, -50%);
  border: ${theme.borderWidths['border-stroke-micro']} solid ${colors['blue-background-strong-default']};
  border-radius: ${radii['corner-radius-circular']};
  background-color: ${colors['cashme-dark-blue']};
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  touch-action: none;

  svg {
    width: 1.75rem;
    height: 1.75rem;
  }
`

export const Caption = styled.span`
  display: block;
  text-align: center;
`

// Rendered only in the mobile layout: "Valor estimado do Imóvel" inside the water (below the 40% floor).
export const WaterEstimate = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12%;
  z-index: ${zIndices[3]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space['spacing-xxs']};
  padding: 0 ${space['spacing-md']};
  text-align: center;
  pointer-events: none;
`

// Rendered only in the mobile layout: the readout that rides alongside the grip.
export const NeedBox = styled.div<{ $isDragging: boolean }>`
  position: absolute;
  left: ${space['spacing-sm']};
  right: calc(15% + ${space['spacing-lg']});
  z-index: ${zIndices[3]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space['spacing-xxs']};
  padding: ${space['spacing-sm']} ${space['spacing-md']};
  text-align: center;
  background-color: ${colors['cashme-dark-blue']};
  border: ${theme.borderWidths['border-stroke-micro']} solid ${colors['blue-border-strong-hover']};
  border-radius: ${radii['corner-radius-md']};
  transform: translateY(-50%);
  transition: ${({ $isDragging }) => ($isDragging ? 'none' : 'top 120ms ease-out')};
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
